import React from 'react';

import { Icon, type IconName } from '../../../../components';
import {
  Section,
  Header,
  HeaderIcon,
  Title,
  OptionalTag,
  Summary,
  Chevron,
  Body,
  BodyClip,
  BodyContent,
} from './IngredientSection.styles';

export type IngredientSectionProps = {
  /** Stable id: names the header / body pair for assistive tech. */
  id: string;
  title: string;
  icon: IconName;
  /** A mark drawn in place of `icon` (e.g. the Lumio mark for a Lumio-driven section); `icon` stays the fallback. */
  iconNode?: React.ReactNode;
  /** Drawn before the icon, e.g. a radio dot when the sections are the choices of a single pick (decorative: the
   *  caller states the choice to assistive tech some other way). */
  leading?: React.ReactNode;
  /** What the section holds, shown in the header while it is closed (already translated, or the owner's own text). */
  summary?: string;
  /** The section is optional: an "(Optional)" tag follows the title while it is open (closed, the summary says so). */
  optional?: boolean;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
};

/**
 * One section of the Ingredients accordion: a header (icon, title, a summary while closed, a round chevron that turns)
 * and a body that grows and fades open. The parent keeps one section open at a time. A closed body stays mounted (its
 * uploads and edits carry on) but is inert, so the keyboard skips what the owner cannot see.
 */
export const IngredientSection = ({
  id,
  title,
  icon,
  iconNode,
  leading,
  summary,
  optional = false,
  open,
  onToggle,
  children,
}: IngredientSectionProps): React.ReactElement => {
  const bodyRef = React.useRef<HTMLDivElement>(null);
  // `inert` is not a typed React 18 prop: mirror the open state onto the DOM attribute.
  React.useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (open) el.removeAttribute('inert');
    else el.setAttribute('inert', '');
  }, [open]);

  const headerId = `${id}-header`;
  const bodyId = `${id}-body`;
  return (
    <Section $open={open} data-open={open ? 'true' : 'false'}>
      <Header type="button" id={headerId} aria-expanded={open} aria-controls={bodyId} onClick={onToggle}>
        {leading}
        <HeaderIcon $open={open} aria-hidden="true">
          {iconNode ?? <Icon name={icon} size={16} />}
        </HeaderIcon>
        <Title raw text={title} tag="span" />
        {optional ? <OptionalTag text="(Optional)" tag="span" $open={open} /> : null}
        {summary ? <Summary raw text={summary} tag="span" $open={open} /> : null}
        <Chevron $open={open} aria-hidden="true">
          <Icon name="chevron-down" size={14} />
        </Chevron>
      </Header>
      <Body $open={open} id={bodyId} role="region" aria-labelledby={headerId} ref={bodyRef}>
        <BodyClip>
          <BodyContent $open={open}>{children}</BodyContent>
        </BodyClip>
      </Body>
    </Section>
  );
};

export default IngredientSection;
